import { useRef, useState } from "react";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MAX_DIM = 1600; // longest edge px after resize
const QUALITY = 0.85; // JPEG quality

/**
 * ImageUploader
 * - Drag/click to pick a local image file (.jpg/.png/.webp/.heic if browser supports)
 * - Resizes client-side to <= MAX_DIM longest edge, returns a data: URI via onChange
 * - Optional `value` prop (data: URI or http URL) is previewed
 *
 * Props:
 *   value: string | null
 *   onChange: (dataUri: string | "") => void
 *   label?: string
 *   testId?: string
 *   aspectClass?: tailwind class for preview aspect (default "aspect-[16/9]")
 */
export default function ImageUploader({ value, onChange, label = "Image", testId = "image-uploader", aspectClass = "aspect-[16/9]" }) {
  const fileRef = useRef(null);
  const [working, setWorking] = useState(false);

  const handlePick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file.");
      return;
    }
    setWorking(true);
    try {
      const dataUri = await resizeToDataUri(file, MAX_DIM, QUALITY);
      onChange(dataUri);
    } catch (err) {
      console.error(err);
      toast.error("Could not read that image. Try another file.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div data-testid={testId}>
      <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">
        {label}
      </label>
      {value ? (
        <div className={`relative ${aspectClass} w-full max-w-md overflow-hidden rounded-xl border border-white/10 bg-[#050505]`}>
          <img src={value} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 flex items-end justify-between gap-2 p-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
            <button
              type="button"
              onClick={handlePick}
              className="bg-electric text-black text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-full hover:bg-[#4ade80] transition inline-flex items-center gap-1.5"
              data-testid={`${testId}-replace`}
            >
              <ImagePlus className="w-3.5 h-3.5" /> Replace
            </button>
            <button
              type="button"
              onClick={() => onChange("")}
              className="bg-white/10 hover:bg-red-500 hover:text-white text-zinc-300 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-full transition inline-flex items-center gap-1.5"
              data-testid={`${testId}-clear`}
            >
              <X className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handlePick}
          disabled={working}
          className={`w-full max-w-md ${aspectClass} rounded-xl border-2 border-dashed border-white/15 bg-[#050505] hover:border-electric hover:bg-electric/5 transition flex flex-col items-center justify-center gap-2 text-zinc-400 hover:text-electric disabled:opacity-60`}
          data-testid={`${testId}-pick`}
        >
          {working ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-xs uppercase tracking-wider">Processing…</span>
            </>
          ) : (
            <>
              <ImagePlus className="w-7 h-7" />
              <span className="text-xs uppercase tracking-wider font-bold">Choose image</span>
              <span className="text-[10px] text-zinc-600">Auto-resized · Stored securely</span>
            </>
          )}
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
        data-testid={`${testId}-input`}
      />
    </div>
  );
}

function resizeToDataUri(file, maxDim, quality) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Image decode failed"));
      img.onload = () => {
        const { width, height } = img;
        let w = width;
        let h = height;
        if (Math.max(w, h) > maxDim) {
          if (w >= h) {
            h = Math.round((h * maxDim) / w);
            w = maxDim;
          } else {
            w = Math.round((w * maxDim) / h);
            h = maxDim;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        try {
          // Always use JPEG to keep payload small + consistent on the wire.
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch (e) {
          reject(e);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
